import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { Fragment, useContext, useState } from 'react';

import { Box, Divider, Flex, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { selectPersonId } from '@/store/user-process/user-selectors.ts';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { bookingApi } from '@/shared/api/booking';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import { IRequestError } from '@/shared/types/app.types';
import { IBookingRadar } from '@/shared/types/booking.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { RadarDetail } from './RadarDetail';
import { useAppSelector } from '@/hooks';

export const Radar = () => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const personId = useAppSelector(selectPersonId);
	// const { user } = useContext(AppContext);
	const [selectedRadar, setSelectedRadar] = useState<IBookingRadar | null>(null);
	const [opened, { close: close, open: open }] = useDisclosure(false);

	const { data: activeRadars } = useQuery({
		queryKey: ['activeRadars', personId],
		queryFn: () => bookingApi.getBookingRadar(personId),
		refetchInterval: 30_000,
		enabled: !!personId,
	});

	const { mutate: deleteRadar, isPending } = useMutation({
		mutationFn: bookingApi.deleteBookingRadar,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['activeRadars'] });
			useShowNotifaction({ text: 'Радар отключен', type: 'success' });
			close();
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const handleOpenRadar = (radar: IBookingRadar) => {
		setSelectedRadar(radar);
		open();
	};

	if (!activeRadars?.some((radar) => !radar.was_completed)) return null;

	return (
		<>
			<LayoutRow
				type="once"
				title={{
					title: {
						children: 'Радар',
						size: 'h4',
						fw: 800,
					},
					button: {
						children: `подробнее`,
					},
				}}
			>
				{activeRadars
					?.filter((radar) => !radar.was_completed)
					.map((radar, _, radars) => (
						<Fragment key={radar?.created_at as string}>
							<Flex
								justify="space-between"
								onClick={() => handleOpenRadar(radar)}
								style={{ cursor: 'pointer' }}
							>
								<Flex direction="column" maw={400}>
									<Text size="md" fw={600}>
										Ищем свободное место
									</Text>
									<Text c="dimmed" size="sm" fw={500}>
										{`Поиск будет активен до ${dayjs(radar?.stop_at).format('DD MMMM HH:mm')}`}
									</Text>
								</Flex>
								{/* <Flex align="center" gap={5}>
									<Loader size="sm" />
								</Flex> */}
							</Flex>
							{radars[radars.length - 1] !== radar && <Divider my={8} color="#6670851A" />}
						</Fragment>
					))}
			</LayoutRow>

			{selectedRadar && (
				<>
					{isMobile ? (
						<BottomSheet
							opened={opened}
							onClose={close}
							title="Радар"
							buttons={[
								{
									children: 'Отключить',
									variant: 'default',
									loading: isPending,
									onClick: () => deleteRadar(selectedRadar.id),
								},
								// { children: 'Редактировать', color: 'green', disabled: true },
							]}
							withBackButton={false}
							isFullScreen
						>
							<RadarDetail type="detail" object={selectedRadar} />
						</BottomSheet>
					) : (
						<ModalSheet
							opened={opened}
							onClose={close}
							title="Радар"
							buttons={[
								{
									children: 'Отключить',
									variant: 'default',
									loading: isPending,
									onClick: () => deleteRadar(selectedRadar.id),
								},
								// { children: 'Редактировать', color: 'green', disabled: true },
							]}
							size="lg"
							centered
						>
							<Box h={600}>
								<RadarDetail type="detail" object={selectedRadar} />
							</Box>
						</ModalSheet>
					)}
				</>
			)}
		</>
	);
};
